import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { getGeoLocation, handleFormSubmission } from '../services/apiService';
import { useNavigate } from 'react-router-dom';

export const UniversalPopup = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    country_code: '+1',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [detectedCountry, setDetectedCountry] = useState(null);

  // Fetch geolocation on component mount
  useEffect(() => {
    const fetchGeoLocation = async () => {
      const geoData = await getGeoLocation();
      if (geoData && geoData.isSuccess) {
        setDetectedCountry(geoData.data.countryName);
      }
    };
    
    if (open) {
      fetchGeoLocation();
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await handleFormSubmission(formData);
      
      if (result.success) {
        toast.success(result.message || 'Thank you! Redirecting...');
        setFormData({ name: '', email: '', country_code: '+1', phone: '', message: '' });
        onOpenChange(false);
        
        // Redirect to application page with customer ID
        setTimeout(() => {
          window.location.href = result.redirectUrl;
        }, 1000);
      } else {
        toast.error(result.message || 'Submission failed. Please try again.');
      }
    } catch (error) {
      toast.error('An unexpected error occurred. Please try again.');
      console.error('Form submission error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]" data-testid="universal-popup">
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading font-bold text-slate-900">
            Get Started Today
          </DialogTitle>
          {detectedCountry && (
            <p className="text-sm text-slate-600 mt-2">
              📍 Detected Location: {detectedCountry}
            </p>
          )}
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="popup-name">Name</Label>
            <Input
              id="popup-name"
              data-testid="popup-name-input"
              placeholder="Enter your full name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="h-12"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="popup-email">Email</Label>
            <Input
              id="popup-email"
              data-testid="popup-email-input"
              type="email"
              placeholder="your.email@example.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="h-12"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="popup-phone">Contact Number</Label>
            <div className="flex gap-2">
              <Select
                value={formData.country_code}
                onValueChange={(value) => setFormData({ ...formData, country_code: value })}
              >
                <SelectTrigger className="w-[120px] h-12" data-testid="popup-country-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="+1">+1 (US)</SelectItem>
                  <SelectItem value="+44">+44 (UK)</SelectItem>
                  <SelectItem value="+91">+91 (IN)</SelectItem>
                  <SelectItem value="+61">+61 (AU)</SelectItem>
                  <SelectItem value="+86">+86 (CN)</SelectItem>
                </SelectContent>
              </Select>
              <Input
                id="popup-phone"
                data-testid="popup-phone-input"
                placeholder="123-456-7890"
                value={formData.phone}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  if (value.length <= 11) {
                    setFormData({ ...formData, phone: value });
                  }
                }}
                required
                className="flex-1 h-12"
                maxLength={11}
                pattern="[0-9]*"
                inputMode="numeric"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="popup-message">Message</Label>
            <Textarea
              id="popup-message"
              data-testid="popup-message-input"
              placeholder="Tell us about your trademark needs..."
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              rows={4}
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="popup-submit-button"
            className="w-full h-12 bg-gradient-to-r from-red-700 to-red-600 hover:from-red-800 hover:to-red-700 text-white font-semibold rounded-full shadow-lg transition-all duration-300"
          >
            {loading ? 'Submitting...' : 'Submit'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
